import { createStyles, withStyles } from '@mui/styles';
import React, { Component } from 'react';
import { GridSortModel, GridSortDirection, GridSortItem, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Theme, Grid, Paper, Container } from '@mui/material';
import UrlConstants from '../../constants/UrlConstants';
import { RootState } from '../../setup';
import Pagination from '../../interfaces/common/pagination';
import Paging from '../../interfaces/common/paging';
import IMenuItem from '../../interfaces/common/menuItem';
import MainLayout from '../common/widgets/mainLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProductFilters from '../../interfaces/output/productFilters';
import { fetchProducts, getProducts, isLoadingProducts } from '../../reducers/productReducer';
import ProductFiltersComponent from '../common/productFilters';
import ProductList from './productList';
import Product from '../../interfaces/output/product';
import LanguageUtils from '../../utils/LanguageUtils';
import PageUtils from '../../utils/pageUtils';
import QuickSearchToolbar from '../common/quickSearchToolbar';

interface IProductsProps {
    products: Pagination<Product>;
    classes: any;
    fetchProductById: any;
    isLoading: boolean;
    onChange: () => void;
    fetchProducts: any;
}

interface IProductsState {
    searchTerm: string;
    filters: ProductFilters;
    paging: Paging;
    menuItem: IMenuItem;
    sortModel: GridSortModel;
    columnVisibilityModel: GridColumnVisibilityModel;
}

const styles = (theme: Theme) => createStyles({
    container: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    paper: {
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column'
    }
});

const messages = {
    products: LanguageUtils.createMessage('Products'),
    add: LanguageUtils.createMessage('Add')
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchProducts: (paging: Paging, filters: ProductFilters) => dispatch(fetchProducts({
        paging,
        filters
    }))
});

const mapStoreToProps = (store: RootState) => {
    return {
        products: getProducts(store),
        isLoading: isLoadingProducts(store)
    };
};

class Products extends Component<IProductsProps, IProductsState> {
    productListRef: any;
    constructor(props: IProductsProps) {
        super(props);
        this.productListRef = React.createRef();

        this.state = {
            searchTerm: '',
            filters: {
                name: ''
            } as ProductFilters,
            paging: PageUtils.getDefaultPaging(),
            menuItem: {
                id: 1,
                name: messages.products,
                icon: <FontAwesomeIcon icon="cube" size="1x" transform="grow-7" />,
                isSelected: false
            },
            sortModel: [{
                field: '',
                sort: '' as GridSortDirection
            } as GridSortItem],
            columnVisibilityModel: { }
        };
    }

    componentDidMount() {
        const { fetchProducts } = this.props;
        const { paging, filters } = this.state;

        fetchProducts(paging, filters);
    }

    onChange = (attribute: string, value: any) => {
        const { filters, paging } = this.state;

        const newFilter = { ...filters } as ProductFilters;
        (newFilter as any)[attribute] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination
        });
    }

    applyFilters = () => {
        const { fetchProducts } = this.props;
        const { paging, filters } = this.state;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };
        this.setState({
            paging: newPagination
        });

        fetchProducts(newPagination, filters);

        if(this.productListRef !== null && this.productListRef !== undefined &&
            this.productListRef.current !== null && this.productListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.productListRef.current.resetDataGridPage();
        }

    }

    setDefaultState = () => {
        const { fetchProducts } = this.props;
        const { paging } = this.state;

        const newFilters = {
            name: ''
        } as ProductFilters;

        const newSortModel = [{
            field: '',
            sort: '' as GridSortDirection
        } as GridSortItem] as GridSortModel;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page,
            sort: newSortModel
        };

        this.setState({
            paging: newPagination,
            filters: newFilters,
            searchTerm: ''
        });

        fetchProducts(newPagination, newFilters);

        if(this.productListRef !== null && this.productListRef !== undefined &&
            this.productListRef.current !== null && this.productListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.productListRef.current.resetDataGridPage();
        }
    }

    onChangeQuickSearch = (value: any) => {
        const { filters, paging } = this.state;
        const { fetchProducts } = this.props;

        const newFilter = { ...filters } as ProductFilters;
        (newFilter as any)['name'] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination,
            searchTerm: value
        });

        fetchProducts(newPagination, newFilter);
    }

    _onSortChange = (newModel: GridSortModel) => {
        const { fetchProducts } = this.props;
        const { sortModel, filters, paging } = this.state;

        if(JSON.stringify(sortModel) !== JSON.stringify(newModel)) {
            const newPagination = {
                ...paging,
                sort: newModel
            };
            this.setState({
                sortModel: newModel,
                paging: newPagination
            });

            fetchProducts(newPagination, filters);
        }
    }

    render() {
        const { classes, products, fetchProducts, isLoading } = this.props;
        const { searchTerm, paging, filters, menuItem, columnVisibilityModel } = this.state;

        return (
            <MainLayout menuItem={menuItem}>
                <Container maxWidth="xl" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <QuickSearchToolbar
                                    onSearch={this.onChangeQuickSearch}
                                    searchTerm={searchTerm}
                                    includeFilters={true}
                                    actions={[{
                                        text: messages.add,
                                        icon: 'plus',
                                        href: `${UrlConstants.PRODUCTS}/add`
                                    }]}
                                >
                                    <ProductFiltersComponent applyFilters={this.applyFilters} setDefaultState={this.setDefaultState} filters={filters} onChange={this.onChange} />
                                </QuickSearchToolbar>
                                <ProductList rows={products} isLoading={isLoading}
                                    ref={this.productListRef}
                                    onPageChange={(nextPage: number, nextSize: number) => {
                                        const newPage = {
                                            ...paging,
                                            page: nextPage,
                                            size: nextSize
                                        };
                                        fetchProducts(newPage, filters);
                                        this.setState({ paging: newPage });
                                    }}
                                    onSortModelChange={(sortModel: GridSortModel) => this._onSortChange(sortModel)}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={(newModel : GridColumnVisibilityModel) =>
                                        this.setState({
                                            columnVisibilityModel: newModel
                                        })
                                    }
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </MainLayout>
        );
    }
}
export default withStyles(styles)(connect(mapStoreToProps, mapDispatchToProps)(Products));